import { useEffect, useId, useRef, useState, useMemo } from 'react'
import { useStore, useBoardState } from '../store/context'
import { inMacShell } from '../lib/shell'
import { errorMessage } from '../api/client'
import { toast } from '../components/toasts'
import { canPush, currentDevice, describeTest, pushRegistration, toDevice, turnOff, turnOn } from './push'
import './push.css'
import type { PushDevice, PushStatus } from '../api/types'

type View =
  /** Nothing to show: a server that does not push, or not known yet. */
  | { state: 'hidden' }
  /** The hub pushes, and this browser cannot show a push: it lacks what it takes, or its worker failed (`problem`). */
  | { state: 'unable'; problem: string | null }
  | { state: 'off' }
  | { state: 'on'; device: PushDevice }

/** What the hub said and what the browser holds, kept for the buttons. */
interface Push {
  key: string
  /** The caller's devices, this one among them once it is on. */
  devices: PushDevice[]
  registration: ServiceWorkerRegistration
}

const toastError = (message: string, err: unknown) => toast(`${message}: ${errorMessage(err)}`, 'error')
/** Today’s words for what reaches the device. */
const PUSHED = 'request is pushed to this device at once and reminded; a blocking one while you are away from the Mac. A failed push is sent again.'

export interface DeviceNotificationsProps {
  /** The control has turned notifications on or off, or sent a test, or tried to: the hub may hold another device, or know how one fares now. */
  onChanged?: () => void
}

export function DeviceNotifications({ onChanged }: DeviceNotificationsProps = {}) {
  const store = useStore()
  const { me } = useBoardState()
  const identity = `${me?.workspace.id}/${me?.member?.id}`
  const api = useMemo(() => store.notifications(), [store, identity])
  const [view, setView] = useState<View>({ state: 'hidden' })
  const [busy, setBusy] = useState<'on' | 'off' | 'test' | null>(null)
  /** The browser refuses to ask: notifications are blocked for the board in its settings. */
  const [blocked, setBlocked] = useState(false)
  const push = useRef<Push | null>(null)
  const titleId = useId()

  useEffect(() => {
    if (inMacShell() || !me?.member) return
    setView({ state: 'hidden' })
    push.current = null
    let gone = false
    void (async () => {
      let status: PushStatus | undefined
      try {
        status = await api.push()
      } catch {
        // A server without the route, or one that did not answer, pushes nothing: no control.
        return
      }
      if (gone || status?.supported !== true || typeof status.key !== 'string' || !status.key) return
      if (!canPush()) {
        setView({ state: 'unable', problem: null })
        return
      }
      const devices = (Array.isArray(status.devices) ? status.devices : []).map(toDevice).filter((d): d is PushDevice => d !== null)
      try {
        // The worker is registered here and nowhere else: only where the server pushes.
        const registration = await pushRegistration()
        const device = await currentDevice(registration, devices, identity)
        if (gone) return
        push.current = { key: status.key, devices, registration }
        setBlocked(Notification.permission === 'denied')
        setView(device ? { state: 'on', device } : { state: 'off' })
      } catch (err) {
        if (!gone) setView({ state: 'unable', problem: errorMessage(err) })
      }
    })()
    return () => {
      gone = true
    }
  }, [api, identity, me?.member])

  const enable = async () => {
    const held = push.current
    if (!held || busy) return
    setBusy('on')
    try {
      // Called straight from the click: a browser asks only then.
      const device = await turnOn(api, identity, held.registration, held.key)
      if (device) {
        held.devices = [...held.devices.filter((d) => d.id !== device.id), device]
        setView({ state: 'on', device })
      }
      setBlocked(Notification.permission === 'denied')
    } catch (err) {
      toastError('Could not turn notifications on', err)
    } finally {
      setBusy(null)
      onChanged?.()
    }
  }

  const disable = async () => {
    const held = push.current
    if (!held || view.state !== 'on' || busy) return
    const { device } = view
    setBusy('off')
    try {
      await turnOff(api, identity, held.registration, device)
      held.devices = held.devices.filter((d) => d.id !== device.id)
      setView({ state: 'off' })
    } catch (err) {
      toastError('Could not turn notifications off', err)
    } finally {
      setBusy(null)
      onChanged?.()
    }
  }

  const test = async () => {
    const held = push.current
    if (!held || busy) return
    setBusy('test')
    try {
      const { text, failed } = describeTest(await api.testPush(), held.devices)
      toast(text, failed ? 'error' : 'info')
    } catch (err) {
      toastError('Could not send a test', err)
    } finally {
      setBusy(null)
      onChanged?.()
    }
  }

  if (view.state === 'hidden') return null
  return (
    <section className="device-notifications" aria-labelledby={titleId}>
      <h2 className="section-title" id={titleId}>
        Notifications on this device
      </h2>
      {view.state === 'unable' &&
        (view.problem ? (
          <p className="muted" role="alert">
            They could not be set up in this browser: {view.problem}
          </p>
        ) : (
          <p className="muted">
            This browser cannot show them. On an iPhone or an iPad (iOS 16.4 or later): add the board to the Home Screen from Safari’s Share
            menu, open it from there, and turn notifications on.
          </p>
        ))}
      {view.state === 'off' && (
        <>
          <p className="muted">Off. When they are on, an urgent {PUSHED}</p>
          {blocked && (
            <p className="muted" role="alert">
              This browser blocks the board’s notifications. Allow them in its settings, then turn them on here.
            </p>
          )}
          <div className="device-notification-actions">
            <button type="button" className="button" onClick={() => void enable()} disabled={busy !== null}>
              {busy === 'on' ? 'Turning on…' : 'Turn on notifications'}
            </button>
          </div>
        </>
      )}
      {view.state === 'on' && (
        <>
          <p className="muted">
            On
            {view.device.label && (
              <>
                , as <strong>{view.device.label}</strong>
              </>
            )}
            . An urgent {PUSHED}
          </p>
          <div className="device-notification-actions">
            <button type="button" className="button" onClick={() => void test()} disabled={busy !== null}>
              {busy === 'test' ? 'Sending…' : 'Send a test'}
            </button>
            <button type="button" className="button button-quiet" onClick={() => void disable()} disabled={busy !== null}>
              {busy === 'off' ? 'Turning off…' : 'Turn off'}
            </button>
          </div>
        </>
      )}
    </section>
  )
}
